import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { Icon } from '../lib/icons';

/**
 * 模拟支付确认页。
 *
 * 这是 MockPay 的 payUrl 指向的页面，用于在没有真实支付资质时
 * 完整跑通「下单 → 支付 → 发货 → 陈列」链路。
 * 接入真实 Stripe / 微信后会替换为对应的收银台。
 */
export default function MockPay() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ duplicated: boolean; offeringsCreated: number } | null>(
    null
  );

  async function confirm() {
    setLoading(true);
    setError('');

    const res = await api<{
      duplicated: boolean;
      offeringsCreated: number;
    }>(`/pay/mock/${orderId}/confirm`, { method: 'POST', body: '{}' });

    setLoading(false);

    if (res.ok && res.data) {
      setResult(res.data);
      setDone(true);
    } else {
      setError(errMsg(res, '支付失败'));
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-ink-100 to-ink-50 px-6">
      <div className="w-full max-w-sm rounded-2xl border border-ink-200 bg-white/90 p-8 text-center shadow-sm">
        {!done ? (
          <>
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-candle-300/30 text-2xl">
              <Icon name="card" />
            </div>
            <h1 className="font-serifcn text-xl text-ink-900">模拟收银台</h1>
            <p className="mt-2 text-xs text-ink-500">
              当前未接入真实支付渠道，此页面用于验证下单发货链路
            </p>
            <p className="mt-4 break-all font-mono text-[10px] text-ink-400">订单号：{orderId}</p>

            {error && (
              <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}

            <button
              onClick={confirm}
              disabled={loading}
              className="mt-6 h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-50"
            >
              {loading ? '处理中…' : '确认支付'}
            </button>

            <button
              onClick={() => navigate(-1)}
              className="mt-3 h-11 w-full rounded-xl border border-ink-200 text-sm text-ink-600 transition hover:bg-ink-100"
            >
              取消
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-2xl">
              <Icon name="candle" />
            </div>
            <h1 className="font-serifcn text-xl text-ink-900">敬献完成</h1>
            <p className="mt-2 text-sm text-ink-600">
              {result?.duplicated
                ? '该订单已处理过，未重复发货（幂等生效）'
                : `已为您敬献 ${result?.offeringsCreated ?? 0} 件祭品`}
            </p>

            <button
              onClick={() => navigate(-2)}
              className="mt-6 h-11 w-full rounded-xl bg-ink-900 text-sm text-ink-50 transition hover:bg-ink-800"
            >
              返回纪念馆
            </button>
          </>
        )}
      </div>
    </div>
  );
}
